import React, { useState, useEffect } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Switch, Typography, TextField,
    useTheme, useMediaQuery, Stack, Card, CardContent,
    InputAdornment, Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { adminApi } from '../../services/api';

const CityManager = () => {
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        fetchCities();
    }, []);

    const fetchCities = async () => {
        try {
            const response = await adminApi.getCities();
            setCities(response.data || []);
        } catch (error) {
            console.error('Error fetching cities:', error);
            setCities([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchToggle = async (cityId, newStatus) => {
        try {
            await adminApi.updateCitySearchStatus(cityId, newStatus);
            // Update local state
            setCities(cities.map(city => 
                city.id === cityId 
                    ? { ...city, search_on: newStatus, updated: new Date().toISOString() }
                    : city
            ));
        } catch (error) {
            console.error('Error updating city search status:', error);
        }
    };

    // Add StatusCount component
    const StatusCount = ({ count, status, label }) => {
        const getColor = () => {
            switch (status) {
                case 'new': return { bg: '#4F46E5', text: '#fff' };
                case 'approved': return { bg: '#059669', text: '#fff' };
                case 'emailed': return { bg: '#D97706', text: '#fff' };
                case 'registered': return { bg: '#16A34A', text: '#fff' };
                default: return { bg: '#F1F5F9', text: '#64748B' };
            }
        };

        const colors = getColor();
        
        return (
            <Tooltip title={label || status}>
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '32px',
                        height: '24px',
                        borderRadius: '12px',
                        backgroundColor: colors.bg,
                        color: colors.text,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        px: 1,
                        opacity: count > 0 ? 1 : 0.35,
                        cursor: 'default'
                    }}
                >
                    {count || 0}
                </Box>
            </Tooltip>
        );
    };

    const CandidateStats = ({ city, isMobile }) => {
        const containerStyles = isMobile ? {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.25,
            mx: 1,
            minWidth: '32px' // Ensure consistent width with desktop view
        } : {
            display: 'flex',
            alignItems: 'center',
            gap: 1
        };

        return (
            <Box sx={containerStyles}>
                <StatusCount count={city.new_count} status="new" label="New Candidates" />
                <StatusCount count={city.approved_count} status="approved" label="Approved Candidates" />
                <StatusCount count={city.emailed_count} status="emailed" label="Emailed Candidates" />
                <StatusCount count={city.registered_count} status="registered" label="Registered Owners" />
            </Box>
        );
    };

    const renderMobileView = () => {
        const filteredCities = Array.isArray(cities) ? cities.filter(city => {
            const searchLower = searchTerm.toLowerCase();
            return (
                city.city_name?.toLowerCase().includes(searchLower) ||
                city.country_name?.toLowerCase().includes(searchLower) ||
                city.code_2?.toLowerCase().includes(searchLower) ||
                city.code_3?.toLowerCase().includes(searchLower)
            );
        }) : [];

        return (
            <Stack spacing={0.5}>
                {filteredCities.map((city) => (
                    <Card 
                        key={city.id} 
                        variant="outlined"
                        sx={{ 
                            '& .MuiCardContent-root': { 
                                p: 1,
                                '&:last-child': { pb: 1 }
                            }
                        }}
                    >
                        <CardContent>
                            <Box sx={{ 
                                display: 'flex',
                                alignItems: 'stretch'
                            }}>
                                <Box sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center'
                                }}>
                                    <Typography 
                                        variant="subtitle2" 
                                        sx={{ 
                                            fontWeight: 500, 
                                            mb: 0.25,
                                            fontSize: '0.875rem',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {city.city_name}
                                    </Typography>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        gap: 0.25
                                    }}>
                                        <Typography 
                                            variant="caption" 
                                            color="textSecondary"
                                            sx={{ 
                                                fontSize: '0.75rem',
                                                lineHeight: 1.2
                                            }}
                                        >
                                            {city.country_name} ({city.country_code})
                                        </Typography>
                                        <Typography 
                                            variant="caption" 
                                            color="textSecondary"
                                            sx={{ 
                                                fontSize: '0.75rem',
                                                lineHeight: 1.2
                                            }}
                                        >
                                            2-letter: {city.code_2} • 3-letter: {city.code_3}
                                        </Typography>
                                    </Box>
                                </Box>
                                <CandidateStats city={city} isMobile={true} />
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    <Switch
                                        checked={Boolean(city.search_on)}
                                        onChange={(e) => handleSearchToggle(city.id, e.target.checked)}
                                        color="primary"
                                        size="small"
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                ))}
            </Stack>
        );
    };

    const renderDesktopView = () => {
        const filteredCities = Array.isArray(cities) ? cities.filter(city => {
            const searchLower = searchTerm.toLowerCase();
            return (
                city.city_name?.toLowerCase().includes(searchLower) ||
                city.country_name?.toLowerCase().includes(searchLower) ||
                city.code_2?.toLowerCase().includes(searchLower) ||
                city.code_3?.toLowerCase().includes(searchLower)
            );
        }) : [];

        return (
            <TableContainer 
                component={Paper}
                sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <Table 
                    size="small"
                    stickyHeader
                    sx={{
                        '& .MuiTableCell-root': {
                            py: 1.5,
                            px: 2
                        }
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell>City</TableCell>
                            <TableCell>Country</TableCell>
                            <TableCell>Codes</TableCell>
                            <TableCell>Candidates</TableCell>
                            <TableCell align="right">Search Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredCities.map((city) => (
                            <TableRow key={city.id}>
                                <TableCell>{city.city_name}</TableCell>
                                <TableCell>
                                    <Box>
                                        <Typography variant="body2">
                                            {city.country_name}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {city.country_code}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">
                                            2-letter: {city.code_2}
                                        </Typography>
                                        <br />
                                        <Typography variant="caption" color="textSecondary">
                                            3-letter: {city.code_3}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <CandidateStats city={city} isMobile={false} />
                                </TableCell>
                                <TableCell align="right">
                                    <Switch
                                        checked={Boolean(city.search_on)}
                                        onChange={(e) => handleSearchToggle(city.id, e.target.checked)}
                                        color="primary"
                                        size={isMobile ? "small" : "medium"}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    if (loading) return <Typography>Loading...</Typography>;

    return (
        <Box sx={{ 
            height: 'calc(100vh - 220px)', 
            display: 'flex', 
            flexDirection: 'column'
        }}>
            {/* Fixed Header Section */}
            <Box sx={{ mb: isMobile ? 1 : 2 }}>
                <Typography 
                    variant={isMobile ? "subtitle1" : "h5"} 
                    gutterBottom
                >
                    City Search Management
                </Typography>
                <TextField
                    placeholder="Search cities..."
                    fullWidth
                    size={isMobile ? "small" : "medium"}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        )
                    }}
                    sx={{ mb: 2 }}
                />
            </Box>

            {/* Scrollable Content Section */}
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                {isMobile ? renderMobileView() : renderDesktopView()}
            </Box>
        </Box>
    );
};

export default CityManager; 